// useWebSocketLocation.js - Hook for real-time GPS tracking via WebSocket
import { useState, useEffect, useRef, useCallback } from 'react';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

export const useWebSocketLocation = (userType = 'admin') => {
  const [isConnected, setIsConnected] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const getToken = useCallback(() => {
    switch (userType) {
      case 'admin':
        return localStorage.getItem('admin_token');
      case 'client':
        return localStorage.getItem('client_portal_token');
      case 'driver':
        return localStorage.getItem('token');
      default:
        return null;
    }
  }, [userType]);

  const getWsEndpoint = useCallback(() => {
    switch (userType) {
      case 'admin':
        return `${WS_URL}/api/ws/admin`;
      case 'client':
        return `${WS_URL}/api/ws/client`;
      case 'driver':
        return `${WS_URL}/api/ws/driver`;
      default:
        return null;
    }
  }, [userType]);

  const connect = useCallback(() => {
    const token = getToken();
    if (!token) {
      setError('No authentication token');
      return;
    }

    const endpoint = getWsEndpoint();
    if (!endpoint) {
      setError('Invalid user type');
      return;
    }

    try {
      // Close existing connection
      if (wsRef.current) {
        wsRef.current.close();
      }

      const ws = new WebSocket(`${endpoint}?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log(`[WebSocket] Connected as ${userType}`);
        setIsConnected(true);
        setError(null);

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'initial_state') {
            // Initial state with all drivers
            setDrivers(message.drivers || []);
            setLastUpdate(new Date());
            console.log(`[WebSocket] Received initial state: ${message.drivers?.length || 0} drivers`);
          } 
          else if (message.type === 'location_update') {
            // Real-time update for a single driver
            setDrivers(prev => {
              const updated = [...prev];
              const index = updated.findIndex(d => d.driver_id === message.driver_id);
              
              if (index >= 0) {
                // Update existing driver
                updated[index] = {
                  ...updated[index],
                  ...message.data
                };
              } else {
                // Add new driver
                updated.push(message.data);
              }
              
              return updated;
            });
            setLastUpdate(new Date());
            console.log(`[WebSocket] Location update for driver ${message.driver_id}`);
          }
          else if (message.type === 'pong') {
            // Ping response - connection is healthy
          }
          else if (message.type === 'connected') {
            console.log(`[WebSocket] Connection confirmed: ${message.message}`);
          }
          else if (message.type === 'ack') {
            // Location acknowledgment (for drivers)
          }
        } catch (err) {
          console.error('[WebSocket] Error parsing message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('[WebSocket] Error:', event);
        setError('Connection error');
      };

      ws.onclose = (event) => {
        console.log(`[WebSocket] Disconnected: ${event.code} - ${event.reason}`);
        setIsConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }

        // Auto-reconnect after 5 seconds (unless intentionally closed)
        if (event.code !== 1000 && event.code !== 4001 && event.code !== 4002) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[WebSocket] Attempting to reconnect...');
            connect();
          }, 5000);
        }
      };

    } catch (err) {
      console.error('[WebSocket] Connection failed:', err);
      setError('Failed to connect');
    }
  }, [getToken, getWsEndpoint, userType]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const refresh = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'refresh' }));
    }
  }, []);

  // Connect on mount
  useEffect(() => {
    connect();
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    drivers,
    lastUpdate,
    error,
    refresh,
    reconnect: connect,
    disconnect
  };
};

// Hook for drivers to send location updates
export const useDriverWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('No authentication token');
      return;
    }

    try {
      if (wsRef.current) {
        wsRef.current.close();
      }

      const ws = new WebSocket(`${WS_URL}/api/ws/driver?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[Driver WS] Connected');
        setIsConnected(true);
        setError(null);
      };

      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        if (message.type === 'connected') {
          console.log('[Driver WS] Connection confirmed');
        }
      };

      ws.onerror = () => {
        setError('Connection error');
      };

      ws.onclose = (event) => {
        console.log('[Driver WS] Disconnected');
        setIsConnected(false);
        
        // Auto-reconnect
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(connect, 5000);
        }
      };

    } catch (err) {
      setError('Failed to connect');
    }
  }, []);

  const sendLocation = useCallback((latitude, longitude, speed, heading, accuracy) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'location',
        latitude,
        longitude,
        speed,
        heading,
        accuracy
      }));
      return true;
    }
    return false;
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000);
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    error,
    connect,
    disconnect,
    sendLocation
  };
};

export default useWebSocketLocation;
